<template>
  <div class="dashboard-container d-flex">
    <!-- Sidebar -->
    <EmployeeSidebar :sidebarOpen="sidebarOpen" currentPage="profile" />

    <!-- Main Content -->
    <div class="main-content flex-grow-1" :class="{ 'sidebar-collapsed': !sidebarOpen }">
      <!-- Navbar -->
      <EmployeeNavbar 
        :title="$t('employee.profile.title')" 
        @toggleSidebar="toggleSidebar" 
        @logout="handleLogout" 
      />

      <!-- Content -->
      <div class="container-fluid p-4">
        <div class="row">
          <!-- Profile Card -->
          <div class="col-lg-4 mb-4">
            <div class="card border-0 shadow-sm">
              <div class="card-body text-center">
                <div class="profile-avatar mb-3">
                  <svg xmlns="http://www.w3.org/2000/svg" width="80" height="80" fill="currentColor" class="bi bi-person-circle" viewBox="0 0 16 16">
                    <path d="M11 6a3 3 0 1 1-6 0 3 3 0 0 1 6 0z" />
                    <path fill-rule="evenodd" d="M0 8a8 8 0 1 1 16 0A8 8 0 0 1 0 8zm8-7a7 7 0 0 0-5.468 11.37C3.242 11.226 4.805 10 8 10s4.757 1.225 5.468 2.37A7 7 0 0 0 8 1z" />
                  </svg>
                </div>
                <h4 class="mb-1">{{ profile.name || 'N/A' }}</h4>
                <p class="text-muted mb-2" v-if="profile.role">{{ profile.role }}</p>
                <span class="badge" :class="profile.status === 'active' ? 'bg-success' : 'bg-danger'" v-if="profile.status">
                  {{ profile.status === 'active' ? 'Active' : 'Inactive' }}
                </span>
                <hr class="my-3" />
                <div class="d-grid gap-2">
                  <button class="btn btn-primary" @click="openEditModal">
                    <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" fill="currentColor" class="bi bi-pencil me-2" viewBox="0 0 16 16">
                      <path d="M12.146.146a.5.5 0 0 1 .708 0l3 3a.5.5 0 0 1 0 .708l-10 10a.5.5 0 0 1-.168.11l-5 2a.5.5 0 0 1-.65-.65l2-5a.5.5 0 0 1 .11-.168l10-10zM11.207 2.5 13.5 4.793 14.793 3.5 12.5 1.207 11.207 2.5zm1.586 3L10.5 3.207 4 9.707V10h.5a.5.5 0 0 1 .5.5v.5h.5a.5.5 0 0 1 .5.5v.5h.293l6.5-6.5zm-9.761 5.175-.106.106-1.528 3.821 3.821-1.528.106-.106A.5.5 0 0 1 5 12.5V12h-.5a.5.5 0 0 1-.5-.5V11h-.5a.5.5 0 0 1-.468-.325z" />
                    </svg>
                    Edit Profile
                  </button>
                  <button class="btn btn-outline-secondary" @click="showPasswordModal = true">
                    <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" fill="currentColor" class="bi bi-key me-2" viewBox="0 0 16 16">
                      <path d="M0 8a4 4 0 0 1 7.465-2H14a.5.5 0 0 1 .354.146l1.5 1.5a.5.5 0 0 1 0 .708l-1.5 1.5a.5.5 0 0 1-.708 0L13 9.207l-.646.647a.5.5 0 0 1-.708 0L11 9.207l-.646.647a.5.5 0 0 1-.708 0L9 9.207l-.646.647A.5.5 0 0 1 8 10h-.535A4 4 0 0 1 0 8zm4-3a3 3 0 1 0 2.712 4.285A.5.5 0 0 1 7.163 9h.63l.853-.854a.5.5 0 0 1 .708 0l.646.647.646-.647a.5.5 0 0 1 .708 0l.646.647.646-.647a.5.5 0 0 1 .708 0l.646.647.793-.793-1-1h-6.63a.5.5 0 0 1-.451-.285A3 3 0 0 0 4 5z" />
                      <path d="M4 8a1 1 0 1 1-2 0 1 1 0 0 1 2 0z" />
                    </svg>
                    Change Password
                  </button>
                </div>
              </div>
            </div>
          </div>

          <!-- Profile Details -->
          <div class="col-lg-8 mb-4">
            <div class="card border-0 shadow-sm mb-4">
              <div class="card-header bg-white border-bottom">
                <h5 class="mb-0">Personal Information</h5>
              </div>
              <div class="card-body">
                <div class="row mb-3">
                  <div class="col-md-6">
                    <label class="text-muted small mb-1">Full Name</label>
                    <p class="fw-semibold">{{ profile.name || 'N/A' }}</p>
                  </div>
                  <div class="col-md-6">
                    <label class="text-muted small mb-1">Email</label>
                    <p class="fw-semibold">{{ profile.email || 'N/A' }}</p>
                  </div>
                </div>
                <div class="row" v-if="profile.mobile_number">
                  <div class="col-md-6">
                    <label class="text-muted small mb-1">Phone</label>
                    <p class="fw-semibold">{{ profile.mobile_number }}</p>
                  </div>
                </div>
              </div>
            </div>

            <div class="card border-0 shadow-sm mb-4">
              <div class="card-header bg-white border-bottom">
                <h5 class="mb-0">Employment Information</h5>
              </div>
              <div class="card-body">
                <div class="row mb-3">
                  <div class="col-md-6" v-if="profile.role">
                    <label class="text-muted small mb-1">Role/Position</label>
                    <p class="fw-semibold">{{ profile.role }}</p>
                  </div>
                  <div class="col-md-6" v-if="profile.pay_rate">
                    <label class="text-muted small mb-1">Pay Rate</label>
                    <p class="fw-semibold">{{ formatCurrency(profile.pay_rate) }}/hour</p>
                  </div>
                </div>
                <div class="row">
                  <div class="col-md-6" v-if="profile.contract_valid_till">
                    <label class="text-muted small mb-1">Contract Valid Till</label>
                    <p class="fw-semibold">{{ formatDate(profile.contract_valid_till) }}</p>
                  </div>
                  <div class="col-md-6" v-if="profile.status">
                    <label class="text-muted small mb-1">Employment Status</label>
                    <p>
                      <span class="badge" :class="profile.status === 'active' ? 'bg-success' : 'bg-danger'">
                        {{ profile.status === 'active' ? 'Active' : 'Inactive' }}
                      </span>
                    </p>
                  </div>
                </div>
              </div>
            </div>

            <!-- Contract Document -->
            <ContractDocument class="mb-4" />
          </div>
        </div>
      </div>
    </div>

    <!-- Edit Profile Modal -->
    <div class="modal fade" :class="{ show: showEditModal, 'd-block': showEditModal }" tabindex="-1" v-if="showEditModal">
      <div class="modal-dialog modal-lg modal-dialog-centered">
        <div class="modal-content">
          <div class="modal-header">
            <h5 class="modal-title">Edit Profile</h5>
            <button type="button" class="btn-close" @click="closeEditModal"></button>
          </div>
          <div class="modal-body">
            <div class="row mb-3">
              <div class="col-md-6">
                <label class="form-label">Full Name</label>
                <input type="text" class="form-control" v-model="editForm.name" />
              </div>
              <div class="col-md-6">
                <label class="form-label">Email</label>
                <input type="email" class="form-control" v-model="editForm.email" />
              </div>
            </div>
            <div class="row mb-3">
              <div class="col-md-6">
                <label class="form-label">Phone</label>
                <input type="tel" class="form-control" v-model="editForm.mobile_number" />
              </div>
            </div>
          </div>
          <div class="modal-footer">
            <button type="button" class="btn btn-secondary" @click="closeEditModal">Cancel</button>
            <button type="button" class="btn btn-primary" @click="saveProfile">Save Changes</button>
          </div>
        </div>
      </div>
    </div>
    <div class="modal-backdrop fade" :class="{ show: showEditModal }" v-if="showEditModal"></div>

    <!-- Change Password Modal -->
    <div class="modal fade" :class="{ show: showPasswordModal, 'd-block': showPasswordModal }" tabindex="-1" v-if="showPasswordModal">
      <div class="modal-dialog modal-dialog-centered">
        <div class="modal-content">
          <div class="modal-header">
            <h5 class="modal-title">Change Password</h5>
            <button type="button" class="btn-close" @click="closePasswordModal"></button>
          </div>
          <div class="modal-body">
            <div class="mb-3">
              <label class="form-label">Current Password</label>
              <input type="password" class="form-control" v-model="passwordForm.current" placeholder="Enter current password" />
            </div>
            <div class="mb-3">
              <label class="form-label">New Password</label>
              <input type="password" class="form-control" v-model="passwordForm.new" placeholder="Enter new password" />
            </div>
            <div class="mb-3">
              <label class="form-label">Confirm New Password</label>
              <input type="password" class="form-control" v-model="passwordForm.confirm" placeholder="Confirm new password" />
            </div>
            <div class="alert alert-info small">
              <strong>Password Requirements:</strong>
              <ul class="mb-0 mt-2">
                <li>At least 8 characters long</li>
                <li>Contains uppercase and lowercase letters</li>
                <li>Contains at least one number</li>
              </ul>
            </div>
          </div>
          <div class="modal-footer">
            <button type="button" class="btn btn-secondary" @click="closePasswordModal">Cancel</button>
            <button type="button" class="btn btn-primary" @click="changePassword">Change Password</button>
          </div>
        </div>
      </div>
    </div>
    <div class="modal-backdrop fade" :class="{ show: showPasswordModal }" v-if="showPasswordModal"></div>
  </div>
</template>

<script>
import EmployeeSidebar from '@/components/EmployeeSidebar.vue';
import EmployeeNavbar from '@/components/EmployeeNavbar.vue';
import ContractDocument from '@/components/ContractDocument.vue';

const API_URL = process.env.VUE_APP_API_URL || "http://localhost:4000";

export default {
  name: "EmployeeProfile",
  components: {
    EmployeeSidebar,
    EmployeeNavbar,
    ContractDocument
  },
  data() {
    return {
      sidebarOpen: true,
      user: { name: "", email: "", role: "employee" },
      showEditModal: false,
      showPasswordModal: false,
      loading: false,
      profile: {},
      editForm: {
        name: "",
        email: "",
        mobile_number: "",
      },
      passwordForm: {
        current: "",
        new: "",
        confirm: "",
      },
    };
  },
  computed: {},
  mounted() {
    const userData = localStorage.getItem("user");
    if (!userData) {
      this.$router.push("/");
      return;
    }
    this.user = JSON.parse(userData);
    if (this.user.role === "admin") {
      this.$router.push("/dashboard");
      return;
    }
    this.loadProfile();
  },
  methods: {
    async loadProfile() {
      this.loading = true;
      try {
        const token = localStorage.getItem("token");
        const response = await fetch(
          `${API_URL}/api/employees/me`,
          {
            headers: {
              Authorization: `Bearer ${token}`,
            },
          }
        );
        if (!response.ok) {
          throw new Error("Failed to fetch profile");
        }
        const data = await response.json();
        this.profile = data;
      } catch (error) {
        console.error("Error loading profile:", error);
        alert("Failed to load profile data");
      } finally {
        this.loading = false;
      }
    },
    toggleSidebar() {
      this.sidebarOpen = !this.sidebarOpen;
    },
    formatCurrency(amount) {
      if (!amount) return 'N/A';
      return new Intl.NumberFormat("de-DE", {
        style: "currency",
        currency: "EUR",
      }).format(amount);
    },
    formatDate(date) {
      if (!date) return 'N/A';
      return new Date(date).toLocaleDateString('de-DE', {
        year: 'numeric',
        month: 'long',
        day: 'numeric'
      });
    },
    closeEditModal() {
      this.showEditModal = false;
    },
    openEditModal() {
      this.editForm = {
        name: this.profile.name,
        email: this.profile.email,
        mobile_number: this.profile.mobile_number || '',
      };
      this.showEditModal = true;
    },
    async saveProfile() {
      this.loading = true;
      try {
        const token = localStorage.getItem("token");
        const response = await fetch(
          `${API_URL}/api/employees/me`,
          {
            method: "PUT",
            headers: {
              "Content-Type": "application/json",
              Authorization: `Bearer ${token}`,
            },
            body: JSON.stringify(this.editForm),
          }
        );

        if (!response.ok) {
          throw new Error("Failed to update profile");
        }

        await this.loadProfile();

        const userData = JSON.parse(localStorage.getItem("user"));
        userData.name = this.editForm.name;
        userData.email = this.editForm.email;
        localStorage.setItem("user", JSON.stringify(userData));
        this.user = userData;

        this.closeEditModal();
        alert("Profile updated successfully!");
      } catch (error) {
        console.error("Error updating profile:", error);
        alert("Failed to update profile");
      } finally {
        this.loading = false;
      }
    },
    closePasswordModal() {
      this.showPasswordModal = false;
      this.passwordForm = { current: "", new: "", confirm: "" };
    },
    changePassword() {
      if (!this.passwordForm.current || !this.passwordForm.new || !this.passwordForm.confirm) {
        alert("Please fill in all password fields");
        return;
      }
      if (this.passwordForm.new !== this.passwordForm.confirm) {
        alert("New passwords do not match");
        return;
      }
      if (this.passwordForm.new.length < 8) {
        alert("Password must be at least 8 characters long");
        return;
      }

      this.closePasswordModal();
      alert("Password changed successfully!");
    },
    handleLogout() {
      localStorage.removeItem("user");
      this.$router.push("/");
    },
  },
};
</script>

<style scoped>
.dashboard-container {
  min-height: 100vh;
  background-color: #f8f9fa;
}

.sidebar {
  width: 280px;
  background-color: #2c3e50;
  color: white;
  min-height: 100vh;
  position: fixed;
  left: 0;
  top: 0;
  overflow-y: auto;
  transition: all 0.3s ease;
  z-index: 1000;
}

.sidebar.collapsed {
  width: 70px;
}

.sidebar-header {
  padding: 2rem 1.5rem;
  border-bottom: 1px solid rgba(255, 255, 255, 0.1);
}

.sidebar-header h4 {
  color: white;
  font-weight: 600;
  font-size: 1.5rem;
}

.sidebar-nav {
  padding: 1rem 0;
}

.nav-item {
  display: flex;
  align-items: center;
  padding: 1rem 1.5rem;
  color: rgba(255, 255, 255, 0.8);
  text-decoration: none;
  transition: all 0.3s ease;
  font-size: 1.05rem;
  gap: 1rem;
}

.nav-item:hover {
  background-color: rgba(255, 255, 255, 0.1);
  color: white;
}

.nav-item.active {
  background-color: rgba(255, 255, 255, 0.15);
  color: white;
  border-left: 4px solid #667eea;
}

.main-content {
  margin-left: 280px;
  min-height: 100vh;
  background-color: #f8f9fa;
  transition: margin-left 0.3s ease;
}

.main-content.sidebar-collapsed {
  margin-left: 70px;
}

.profile-avatar {
  color: #667eea;
}

.modal.show {
  background-color: rgba(0, 0, 0, 0.5);
}

@media (max-width: 768px) {
  .sidebar {
    transform: translateX(-100%);
  }

  .sidebar:not(.collapsed) {
    transform: translateX(0);
  }

  .main-content {
    margin-left: 0 !important;
  }
}
</style>
